import { createFileRoute, Link } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { DogBoxQuoteForm } from "@/components/DogBoxQuoteForm";
import hero from "@/assets/custom-welded-aluminum-dog-box-hunting-truck-seguin-tx.jpg";
import heroPicture from "@/assets/custom-welded-aluminum-dog-box-hunting-truck-seguin-tx.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import welding from "@/assets/tig-mig-aluminum-dog-box-welding-seguin-tx-shop.jpg";
import hobartWelding from "@/assets/hobart-tig-welder-aluminum-dog-box-fabrication-seguin-tx.jpg";
import huntingTeam from "@/assets/texas-hunters-orange-vests-custom-dog-box-hunting-truck.jpg";
import huntingTeamPicture from "@/assets/texas-hunters-orange-vests-custom-dog-box-hunting-truck.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import quailHuntingTruck from "@/assets/hunting-truck-quail-hunters-flatbed-dog-box-sunset.png";
import birdDogsFaq from "@/assets/bird-dogs-faq.jpg";





import huntingTruckHighSeats from "@/assets/custom-hunting-truck-orange-tarp-high-seats-dog-box-seguin.jpg";
import dogBoxTruckRear from "@/assets/dog-box-truck-rear.jpg";
import aluminumDogBoxOpenLid from "@/assets/aluminum-dog-box-open-lid.jpg";

export const Route = createFileRoute("/dog-boxes")({
  head: (ctx) => ({
    links: [
      canonicalLink(pathFromMatch(ctx)),
    ],
    meta: routeMeta(ctx, {
      title: "Welded Aluminum Flatbed Rigs & Custom Dog Boxes for Hunting Trucks | Seguin, TX",
      description:
        "Welded aluminum flatbed rigs, custom dog boxes and hunting truck builds — TIG/MIG fabricated diamond plate flatbeds, vented dog boxes, high-seat towers.",
      image: hero,
      imageAlt: "Welded aluminum flatbed rig with custom diamond plate dog box, high-seat tower and vented kennel doors — built in Seguin, TX by Texas Made Shade",
      socialTitle: "Welded Aluminum Flatbed Rigs & Custom Dog Boxes — Hunting Truck Builds in Seguin, TX",
      socialDescription:
        "TIG/MIG-welded aluminum flatbed rigs and custom dog boxes for hunting trucks. Diamond plate flatbeds, vented kennel doors, high-seat towers, gun racks and gear racks — fabricated in our Seguin, Texas welding shop.",
      keywords:
        "welded aluminum flatbed rig, aluminum flatbed hunting truck, custom flatbed dog box rig, welded flatbed for hunting truck, diamond plate aluminum flatbed, dog boxes for hunting, dog box for hunting truck, hunting truck dog box, dog hunting trucks, custom hunting trucks, hunting truck builds, hunting truck fabrication, custom dog boxes Texas, aluminum dog box, steel dog box, welded dog box, dog box welding Texas, high-seat tower, gun racks, gear racks, mule buggy dog box, UTV dog box, ranch truck builds, Seguin TX welding, San Antonio dog box, South Texas hunting trucks",
    }),
    scripts: serviceHeadScripts({
      path: "/dog-boxes",
      serviceName: "Custom Aluminum Dog Boxes",
      breadcrumbName: "Dog Boxes",
      description: "Welded aluminum dog boxes for hunting trucks and UTVs — custom-built in Seguin, TX with diamond plate, gun racks and high seats.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/dog-boxes"
      bodyLayout="accordion"
      faqMedia={
        <figure className="mb-2 overflow-hidden border-4 border-navy-deep shadow-card sm:float-right sm:ml-6 sm:mb-3 sm:w-[44%] md:w-[38%] lg:w-[34%]">
          <img
            src={birdDogsFaq}
            alt="English Setter and Boykin Spaniel bird dogs sitting on a South Texas ranch — the kind of dogs that ride in our custom-welded aluminum dog boxes"
            width={647}
            height={970}
            loading="lazy"
            decoding="async"
            className="block h-auto w-full object-cover"
          />
          <figcaption className="bg-navy-deep px-4 py-2 font-display text-[11px] uppercase tracking-[0.2em] text-white">
            Built for the dogs that hunt out of them
          </figcaption>
        </figure>
      }
      serviceName="Custom Dog Boxes"
      breadcrumbs={[
        { label: "Welding & Custom Fabrication", to: "/custom-fabrication" },
        { label: "Custom Dog Boxes & Hunting Truck Welding" },
      ]}
      eyebrow="★ DOG BOXES & HUNTING TRUCKS ★"
      title="Welded Aluminum Flatbed Rigs & Custom Dog Boxes for Hunting Trucks — Seguin, TX"
      intro="Welded aluminum flatbed rigs, custom dog boxes and full hunting truck builds — TIG/MIG fabricated in Seguin, Texas. Diamond plate aluminum flatbeds, vented kennel doors, high-seat towers, gun racks, gear racks and tie-downs welded in-house, then paired with marine-vinyl truck upholstery and fitted wrap tarps for a complete dog hunting truck. Serving San Antonio, Austin, the Hill Country, South Texas and the Gulf Coast."
      heroImage={heroPicture}
      hideLetsTalkForm
      hideRelatedLinks
      heroCta={{
        eyebrow: "Aluminum & Steel Dog Box Welding · Seguin, TX",
        headline: "Building a Custom Hunting Truck? Get a Free Dog Box Quote.",
        subhead:
          "Tell us your truck make/model, bed size, number of dogs, and any extras (top storage, gun racks, gear racks, vented doors). We'll come back same business day with sizing questions and a no-pressure written estimate.",
        primaryHref: `#dog-box-quote`,
        primaryLabel: "Get My Free Dog Box Quote",
        phoneNumber: "+1 (830) 390-9588",
        image: {
          src: huntingTeamPicture,
          alt: "Four Texas bird hunters in blaze-orange vests standing on a custom welded aluminum dog box hunting truck at sunset",
          caption: "Built for Texas hunters — dog boxes, truck rigs & gear",
        },
      }}
      features={[
        "Custom aluminum & steel dog boxes for hunting trucks — sized to your bed",
        "Vented kennel doors with secure latches and weather seals",
        "Top storage, tie-downs, welded gear racks and gun racks",
        "TIG/MIG dog box welding in our Seguin, TX shop — no kit boxes",
        "UTV & mule buggy dog boxes for ranch leases and back-country hunts",
        "Marine-vinyl seat re-covers, headliners and truck upholstery",
        "Fitted wrap tarps for dog boxes, beds and gear racks",
        "Powder coat, paint or raw mill finish — built for South Texas brush and weather",
      ]}
      body={[
        {
          heading: "Custom Aluminum Dog Boxes for Hunting Trucks",
          text: "Lightweight aluminum dog boxes welded in-house and patterned to your specific truck bed — short bed, long bed, dually or flatbed.\nWe build with diamond plate or smooth aluminum, vented doors, drain channels and weather seals.\nYour dogs ride cool, dry and quiet from the lease to the gas pump.",
          bullets: [
            "TIG-welded aluminum frames — corrosion-resistant for South Texas humidity",
            "Diamond plate or smooth aluminum panel options",
            "Single, double or triple compartments sized to your dog count",
            "Vented doors with adjustable louvers and secure paddle latches",
            "Drain channels & rubber floor mats for easy wash-out",
          ],
          cta: { label: "See Welding & Custom Fabrication Services", href: "/custom-fabrication" },
        },
        {
          heading: "Steel Dog Boxes for Heavy-Duty Ranch & Hunting Rigs",
          text: "When the build calls for steel — bigger dogs, heavy gear, ranch use, or a long working life on rough roads — we MIG weld carbon steel dog boxes.\nReinforced floors, lift handles and integrated tie-down points come standard.\nPowder coated or painted in your color of choice.",
          bullets: [
            "MIG-welded steel construction with reinforced floor pans",
            "Integrated lift handles and D-ring tie-down points",
            "Powder coat or industrial paint finish",
            "Sized for ranch & working dogs — big-frame compartments",
          ],
        },
        {
          heading: "Top Storage, Gun Racks, Gear Racks & Tie-Downs",
          text: "We weld in the storage features that make a hunting truck actually work in the field.\nTop storage compartments for shells and decoys, locking gun racks, removable gear racks.\nTie-down rails for coolers, decoy bags and game bags.",
          bullets: [
            "Top storage compartments with locking lids",
            "Welded-in gun racks with locking hasps",
            "Removable gear racks for coolers, decoy bags & game bags",
            "Tie-down rails, D-rings and shock-cord channels",
            "Pair with our hunting & fishing canvas covers and gear bags",
          ],
          cta: { label: "Hunting & Fishing Canvas, Gear Bags & Truck Wraps", href: "/hunting-fishing-covers" },
        },
        {
          heading: "Full Custom Hunting Truck Builds — Welding + Upholstery + Canvas",
          text: "From welding to canvas, we build hunting trucks start-to-finish under one roof in Seguin.\nCombine a custom dog box with marine-vinyl seat re-covers, a fitted wrap tarp and a hunting trailer cover.\nYou've got a complete rig — built to take mud, blood, brush and seasons of hard use.",
          bullets: [
            "Marine-vinyl seat re-covers & headliners built for hunting trucks",
            "Fitted wrap tarps for dog boxes, beds and gear racks",
            "Custom canvas dog hunting belt accessories — shell carriers, e-collar pouches, lead loops",
            "Pair with our hunting trailer covers and ranch equipment tarps",
          ],
          cta: { label: "Hunting Trailer & Ranch Equipment Tarps", href: "/trailer-covers" },
        },
        {
          heading: "Aluminum Welding — TIG & MIG Fabrication in Seguin, TX",
          text: "Beyond dog boxes, our in-house aluminum welding shop builds custom frames, canopies, brackets and one-off pieces for hunting trucks, trailers, boats and ranch rigs.\nTIG for clean, structural aluminum work; MIG for heavier steel and production runs.\nPatterned, welded and finished under one roof.",
          bullets: [
            "TIG aluminum welding — clean structural welds for frames & canopies",
            "MIG welding for steel frames, racks and heavy-duty fabrication",
            "Custom aluminum canopies, awning frames and shade structures",
            "Hunting truck racks, brackets, gun mounts and tie-down hardware",
            "Trailer, boat and ranch equipment repairs & one-off builds",
          ],
          cta: { label: "Get a Free Aluminum Welding Quote", href: `/#lets-talk?type=${encodeURIComponent("Aluminum Welding / Fabrication")}&notes=${encodeURIComponent("Aluminum welding inquiry — please describe your project (frame, canopy, bracket, repair), materials, and approximate dimensions.")}` },
        },
      ]}
      applicationsEyebrow="WHAT WE BUILD"
      applicationsHeading="Custom Dog Box & Hunting Truck Products"
      applications={[
        "Aluminum Dog Boxes",
        "Steel Dog Boxes",
        "Single, Double & Triple Compartments",
        "Top Storage Boxes",
        "Welded Gun Racks",
        "Removable Gear Racks",
        "Marine-Vinyl Truck Upholstery",
        "Fitted Wrap Tarps",
        "Mule Buggy & UTV Dog Boxes",
        "Ranch Truck Builds",
      ]}
      cityCallouts={[]}
      faqs={[
        {
          q: "Do you build custom dog boxes for hunting trucks in Texas?",
          a: "Yes — we weld custom aluminum and steel dog boxes sized to your specific truck bed (short bed, long bed, dually, flatbed). Vented doors, top storage, gun racks, gear racks and tie-downs are all built in-house in our Seguin, TX welding and canvas shop.",
          aJsx: (
            <p>
              Yes — we weld <Link to="/dog-boxes" className="font-semibold text-navy-deep underline">custom aluminum and steel dog boxes</Link> sized to your specific truck bed (short bed, long bed, dually, flatbed). Vented doors, top storage, gun racks, gear racks and tie-downs are all built in-house in{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">our Seguin, TX welding and canvas shop</Link>.
            </p>
          ),
        },
        {
          q: "Aluminum or steel — which is better for a hunting truck dog box?",
          a: "Aluminum is the most popular choice — lighter weight, naturally corrosion-resistant, and easier on fuel mileage. Steel is the right call for heavy-duty ranch use, bigger framed dogs, or when the rig will see years of hard work and you want maximum durability. We build both, and we'll recommend the right material for your hunt.",
        },
        {
          q: "Can you weld in a gun rack and top storage on the dog box?",
          a: "Yes — locking gun racks, top storage compartments with weather-sealed lids, removable gear racks for coolers and decoy bags, and tie-down rails for shock cords or ratchet straps. Tell us how you hunt and we'll lay out the storage to match.",
          aJsx: (
            <p>
              Yes — locking gun racks, top storage compartments with weather-sealed lids, removable gear racks for coolers and decoy bags, and tie-down rails for shock cords or ratchet straps. Pair the box with our{" "}
              <Link to="/hunting-fishing-covers" className="font-semibold text-navy-deep underline">hunting &amp; fishing canvas, gear bags and truck wrap tarps</Link> for a complete rig.
            </p>
          ),
        },
        {
          q: "Do you build dog boxes for mule buggies, UTVs and side-by-sides?",
          a: "Yes — scaled-down aluminum dog boxes for Polaris Ranger, Can-Am Defender, Kawasaki Mule, John Deere Gator and similar UTVs and side-by-sides, plus mule buggy and lease vehicle dog boxes. Pair with our roll-down weather curtains for cold-weather hunts.",
          aJsx: (
            <p>
              Yes — scaled-down aluminum dog boxes for Polaris Ranger, Can-Am Defender, Kawasaki Mule, John Deere Gator and similar UTVs and side-by-sides, plus mule buggy and lease vehicle dog boxes. Pair with our{" "}
              <Link to="/curtains" className="font-semibold text-navy-deep underline">roll-down UTV weather curtains</Link> for cold-weather hunts.
            </p>
          ),
        },
        {
          q: "Do you build the rest of the hunting truck — upholstery, wrap tarps, gear bags?",
          a: "Yes. We're a full canvas + welding shop, so we can build the dog box, re-cover the seats in marine vinyl, sew a fitted wrap tarp, and stitch up matching gear bags and shell carriers — all under one roof in Seguin.",
          aJsx: (
            <p>
              Yes. We're a full canvas + welding shop, so we can build the dog box, re-cover the seats in marine vinyl, sew a fitted wrap tarp, and stitch up matching{" "}
              <Link to="/hunting-fishing-covers" className="font-semibold text-navy-deep underline">hunting gear bags, shell carriers and dog hunting belt accessories</Link> — all under one roof in{" "}
              <Link to="/seguin" className="font-semibold text-navy-deep underline">Seguin</Link>.
            </p>
          ),
        },
        {
          q: "What Texas areas do you serve for custom dog boxes and hunting truck builds?",
          a: "We're based in Seguin and build for hunters across Central Texas, South Texas, the Hill Country and the Gulf Coast — including San Antonio, New Braunfels, Boerne, Kerrville, Fredericksburg, Austin, Houston and Corpus Christi.",
          aJsx: (
            <p>
              We're based in <Link to="/seguin" className="font-semibold text-navy-deep underline">Seguin</Link> and build for hunters across Central Texas, South Texas, the Hill Country and the Gulf Coast — including{" "}
              <Link to="/san-antonio" className="font-semibold text-navy-deep underline">San Antonio</Link>,{" "}
              <Link to="/new-braunfels" className="font-semibold text-navy-deep underline">New Braunfels</Link>,{" "}
              <Link to="/boerne" className="font-semibold text-navy-deep underline">Boerne</Link>,{" "}
              <Link to="/kerrville" className="font-semibold text-navy-deep underline">Kerrville</Link>,{" "}
              <Link to="/fredericksburg" className="font-semibold text-navy-deep underline">Fredericksburg</Link>,{" "}
              <Link to="/austin" className="font-semibold text-navy-deep underline">Austin</Link>,{" "}
              <Link to="/houston" className="font-semibold text-navy-deep underline">Houston</Link> and{" "}
              <Link to="/corpus-christi" className="font-semibold text-navy-deep underline">Corpus Christi</Link>.
            </p>
          ),
        },
      ]}
      postCta={(
        <section className="border-t-4 border-navy-deep bg-white py-20">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <div className="text-center">
              <div className="font-display text-sm tracking-[0.3em] text-red">
                ★ RELATED SERVICES
              </div>
              <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
                Pairs Well With
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
                Build out the rest of your hunting truck with canvas, welding and shade work
                from the same Seguin shop.
              </p>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <Link to="/hunting-fishing-covers" className="group block border-l-4 border-red bg-cream p-6 shadow-card transition hover:bg-navy-deep hover:text-white">
                <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">Hunting &amp; Fishing Canvas</h3>
                <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">Gear bags, shell carriers, e-collar pouches, dog hunting belt accessories and truck wrap tarps — sewn to match your rig.</p>
              </Link>
              <Link to="/trailer-covers" className="group block border-l-4 border-red bg-cream p-6 shadow-card transition hover:bg-navy-deep hover:text-white">
                <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">Hunting Trailer &amp; Equipment Tarps</h3>
                <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">Fitted trailer covers, ranch equipment tarps and heavy-duty wrap tarps built to take Texas weather and brush.</p>
              </Link>
              <Link to="/custom-fabrication" className="group block border-l-4 border-red bg-cream p-6 shadow-card transition hover:bg-navy-deep hover:text-white">
                <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">Welding &amp; Custom Fabrication</h3>
                <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">TIG &amp; MIG aluminum and steel welding — high seats, racks, brackets, gun mounts and one-off hunting truck pieces.</p>
              </Link>
              <Link to="/curtains" className="group block border-l-4 border-red bg-cream p-6 shadow-card transition hover:bg-navy-deep hover:text-white">
                <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">Roll-Down UTV &amp; Truck Curtains</h3>
                <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">Clear vinyl and canvas roll-down curtains for mule buggies, UTVs and hunting trucks — cold-weather and rain protection.</p>
              </Link>
              <Link to="/boat-covers" className="group block border-l-4 border-red bg-cream p-6 shadow-card transition hover:bg-navy-deep hover:text-white">
                <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">Boat Covers &amp; Marine Canvas</h3>
                <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">Custom boat covers, bimini tops and marine canvas for duck boats, bay boats and offshore rigs across the Texas coast.</p>
              </Link>
              <Link to="/seguin" className="group block border-l-4 border-red bg-cream p-6 shadow-card transition hover:bg-navy-deep hover:text-white">
                <h3 className="font-display text-xl font-bold text-navy-deep group-hover:text-white">Visit the Seguin, TX Shop</h3>
                <p className="mt-2 text-sm text-muted-foreground group-hover:text-white/90">Drive in for on-site fitment — welding, upholstery and canvas all under one roof in Seguin, Texas.</p>
              </Link>
            </div>
          </div>
        </section>
      )}
      afterWhyUs={(
        <section className="border-t-4 border-navy-deep bg-white py-16">
          <div className="mx-auto max-w-5xl px-4 lg:px-8">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">
              ★ Aluminum &amp; Steel Dog Box Welding · Seguin, TX
            </div>
            <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
              Custom Welded Aluminum Dog Boxes Built for Texas Hunting Trucks
            </h2>
            <figure className="mt-6 sm:float-right sm:ml-6 sm:mb-3 sm:mt-4 sm:w-[48%] md:w-[44%] lg:w-[42%]">
              <img
                src={quailHuntingTruck}
                alt="South Texas quail hunting party in blaze-orange vests with English Pointers in a custom welded aluminum dog box on a camo flatbed hunting truck at sunset in the brush country — built by Texas Made Shade in Seguin, TX"
                width={1456}
                height={1100}
                loading="lazy"
                decoding="async"
                className="block h-auto w-full border-4 border-navy-deep object-cover shadow-card"
              />
              <figcaption className="mt-2 text-xs uppercase tracking-[0.2em] text-navy-deep/70">
                South Texas quail rig — welded flatbed, dog box &amp; high seats
              </figcaption>
            </figure>
            <p className="mt-4 text-lg text-navy-deep/80">
              Every dog box we build starts on the shop floor in Seguin — patterned to your
              truck, TIG- or MIG-welded in aluminum or steel, and finished with the racks,
              doors and tie-downs that make a hunting rig actually work in the field. No kit
              boxes, no outsourced welding.
            </p>
            <p className="mt-4 text-lg text-navy-deep/80">
              Whether you're running quail in the South Texas brush, axis and whitetail in
              the Hill Country, or retrievers on the Gulf Coast, your rig gets built the
              same way — diamond plate decking, vented kennel doors with secure latches,
              drain channels, and welded-in gun racks, gear racks and high-seat towers laid
              out to fit how your crew loads, hauls and hunts.
            </p>
            <figure className="mt-6 sm:float-left sm:clear-left sm:mr-6 sm:mb-3 sm:mt-4 sm:w-[42%] md:w-[38%] lg:w-[36%]">
              <img
                src={aluminumDogBoxOpenLid}
                alt="Custom welded diamond plate aluminum dog box with gas-strut top storage lid open, twin vented kennel doors, side vents and blue interior — built by Texas Made Shade in Seguin, TX"
                width={734}
                height={650}
                loading="lazy"
                decoding="async"
                className="block h-auto w-full border-4 border-navy-deep object-cover shadow-card"
              />
              <figcaption className="mt-2 text-xs uppercase tracking-[0.2em] text-navy-deep/70">
                Diamond plate dog box — gas-strut top storage &amp; vented doors
              </figcaption>
            </figure>
            <p className="mt-4 text-lg text-navy-deep/80">
              Pair the welded dog box with a fitted wrap tarp, marine-vinyl seat re-covers
              and matching gear bags — sewn under the same roof — and your truck rolls off
              the lot as one complete, weather-tight hunting rig.
            </p>
            <div className="clear-both" />
          </div>
        </section>
      )}
    >

      <section className="border-t-4 border-navy-deep bg-cream py-20">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="max-w-3xl">
            <div className="font-display text-sm tracking-[0.3em] text-red">
              ★ WHY US
            </div>
            <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
              Why Texas Hunters Choose Our Seguin Welding &amp; Canvas Shop
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              No outsourcing. No kit boxes. No guessing on fitment. Hunters across Texas
              drive in because the dog box, the upholstery, the wrap tarp and the gear
              bags all get built by the same shop — to fit the same truck.
            </p>
          </div>

          <dl className="mt-8 grid grid-cols-2 gap-px overflow-hidden border-2 border-navy-deep bg-navy-deep sm:grid-cols-3">
            {[
              { stat: "5.0★", label: "Google Reviews" },
              { stat: "20+ yrs", label: "Welding & Canvas in Seguin" },
              { stat: "30+", label: "Texas Counties Served" },
            ].map((p) => (
              <div key={p.label} className="bg-cream px-3 py-4 text-center sm:px-4 sm:py-5">
                <div className="font-display text-2xl font-bold leading-none text-red sm:text-3xl md:text-4xl">
                  {p.stat}
                </div>
                <div className="mt-2 font-display text-[10px] uppercase leading-tight tracking-[0.18em] text-navy-deep/80 sm:text-[11px] sm:tracking-[0.2em]">
                  {p.label}
                </div>
              </div>
            ))}
          </dl>

          <div className="mt-10 grid items-start gap-8 md:grid-cols-2">
            <figure className="overflow-hidden border-4 border-navy-deep shadow-card">
              <img
                src={hobartWelding}
                alt="Texas Made Shade fabricator in a Hobart welding hood TIG-welding an aluminum dog box frame with bright blue arc and sparks inside the Seguin, TX shop"
                width={1200}
                height={1600}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
              <figcaption className="bg-navy-deep px-4 py-3 font-display text-xs uppercase tracking-[0.2em] text-white">
                In-house TIG &amp; MIG welding — Seguin, TX
              </figcaption>
            </figure>

            <div className="grid gap-4">
              {[
                { h: "Custom Dog Boxes", p: "Aluminum or steel — patterned to your truck bed, not a kit." },
                { h: "In-House Welding", p: "TIG &amp; MIG welded on-site. No outsourcing, no middlemen." },
                { h: "Full Hunting Trucks", p: "Dog box + marine-vinyl upholstery + wrap tarp + gear bags." },
                { h: "Gun &amp; Gear Racks", p: "Locking gun racks and removable gear racks welded in to fit." },
                { h: "UTV &amp; Mule Buggies", p: "Scaled dog boxes for Ranger, Defender, Mule, Gator &amp; buggies." },
                { h: "Built for Texas", p: "South Texas brush, Hill Country leases, Gulf Coast humidity." },
              ].map((item) => (
                <div
                  key={item.h}
                  className="flex gap-4 border-l-4 border-red bg-white p-4 shadow-card"
                >
                  <span className="font-display text-2xl leading-none text-red">★</span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-navy-deep">
                      {item.h.replace(/&amp;/g, "&")}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {item.p.replace(/&amp;/g, "&")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t-4 border-navy-deep bg-white py-20">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="font-display text-sm tracking-[0.3em] text-red">★ HUNTING TRUCK GALLERY</div>
          <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
            Welded Aluminum Flatbed Rigs, Diamond Plate Dog Boxes &amp; High-Seat Towers
          </h2>

          <div className="mt-8 grid items-start gap-8 lg:grid-cols-[1fr_minmax(0,360px)] lg:gap-12">
            <div>
              <figure className="mb-3 overflow-hidden border-4 border-navy-deep shadow-card sm:float-left sm:mr-5 sm:mb-2 sm:w-[55%] md:w-[50%]">
                <img
                  src={dogBoxTruckRear}
                  alt="Custom welded diamond plate aluminum dog box on a 4x4 hunting truck — twin vented kennel doors with diamond mesh, side vent, roof rack and top storage lid — built by Texas Made Shade in Seguin, TX"
                  width={1200}
                  height={700}
                  loading="lazy"
                  decoding="async"
                  className="block h-auto w-full object-cover"
                />
                <figcaption className="bg-navy-deep px-4 py-2 font-display text-[11px] uppercase tracking-[0.2em] text-white">
                  Diamond plate dog box — twin vented kennel doors &amp; roof rack
                </figcaption>
              </figure>
              <p className="text-base text-muted-foreground md:text-lg">
                Welded aluminum flatbed rigs, diamond plate dog boxes, high-seat towers,
                gun racks and orange canvas ID panels — patterned, welded and sewn in our
                Seguin, Texas shop for South Texas brush country, Hill Country leases and
                Coastal Bend duck and dove rigs.
              </p>

              <h3 className="mt-8 font-display text-lg font-bold text-navy-deep md:text-xl">
                Aluminum Flatbeds, Welded Dog Boxes &amp; Canvas ID Panels
              </h3>
              <ul className="mt-3 grid gap-2 text-sm text-navy-deep sm:grid-cols-2 sm:text-base">
                <li className="flex gap-2"><span className="text-red">▸</span> TIG- &amp; MIG-welded in-house</li>
                <li className="flex gap-2"><span className="text-red">▸</span> Diamond plate decking</li>
                <li className="flex gap-2"><span className="text-red">▸</span> Vented kennel doors</li>
                <li className="flex gap-2"><span className="text-red">▸</span> Locking gun racks</li>
                <li className="flex gap-2"><span className="text-red">▸</span> Sewn wrap tarps</li>
                <li className="flex gap-2"><span className="text-red">▸</span> Brass-grommeted canvas panels</li>
              </ul>
              <p className="mt-3 text-sm italic text-muted-foreground">
                Built start-to-finish under one roof — fitment, fabric and finish all match.
              </p>
            </div>

            <figure className="overflow-hidden border-4 border-navy-deep shadow-card lg:sticky lg:top-24">
              <img
                src={huntingTruckHighSeats}
                alt="Custom hunting truck flatbed rig with welded aluminum high-seat tower, diamond plate toolboxes and orange '7P' canvas identification panel — built by Texas Made Shade in Seguin, TX"
                width={1200}
                height={1600}
                loading="lazy"
                decoding="async"
                className="block h-auto w-full object-cover"
              />
              <figcaption className="bg-navy-deep px-4 py-2 font-display text-[11px] uppercase tracking-[0.2em] text-white">
                Custom hunting truck — high seats, dog box &amp; canvas panel
              </figcaption>
            </figure>
          </div>
        </div>
      </section>


      <section className="border-t-4 border-navy-deep bg-navy-deep py-16 text-white">
        <div className="mx-auto max-w-4xl px-4 text-center lg:px-8">
          <div className="font-display text-sm tracking-[0.3em] text-red">
            ★ READY TO BUILD YOURS?
          </div>
          <h2 className="mt-2 font-display text-4xl font-bold md:text-5xl">
            Get a Free Dog Box Quote — or Call for Build Availability
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-white/85">
            Tell us your truck make/model, bed size, dog count and any extras (top storage,
            gun racks, vented doors). Same-business-day reply with sizing questions and a
            no-pressure written estimate from our Seguin, TX welding shop.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={`#dog-box-quote`}
              className="inline-flex items-center justify-center border-4 border-red bg-red px-8 py-4 font-display text-base font-bold uppercase tracking-wider text-white shadow-card transition hover:bg-white hover:text-red"
            >
              Request My Free Quote
            </a>
            <a
              href="tel:+18303909588"
              className="inline-flex items-center justify-center border-4 border-white bg-transparent px-8 py-4 font-display text-base font-bold uppercase tracking-wider text-white transition hover:bg-white hover:text-navy-deep"
            >
              Call +1 (830) 390-9588
            </a>
          </div>
          <p className="mt-6 text-sm text-white/70">
            Mon–Fri · Seguin, TX · Serving San Antonio, Austin, the Hill Country, South Texas &amp; the Gulf Coast
          </p>
        </div>
      </section>

      <section className="border-t-4 border-navy-deep bg-cream py-20">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="font-display text-sm tracking-[0.3em] text-red">★ TEXAS SERVICE AREAS</div>
          <h2 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
            Custom Dog Boxes &amp; Hunting Truck Builds Across Texas
          </h2>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            Based in Seguin, we weld and ship custom dog boxes statewide and install hunting
            truck builds for clients across Central Texas, South Texas, the Hill Country and the
            Gulf Coast.
          </p>

          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <article className="border-l-4 border-red bg-white p-6 shadow-card">
              <h3 className="font-display text-xl font-bold text-navy-deep">
                Dog Boxes in San Antonio &amp; New Braunfels
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Aluminum and steel dog boxes for South Central Texas hunters — bird hunters in
                the brush country, hog hunters around <Link to="/san-antonio" className="font-semibold text-navy-deep underline">San Antonio</Link> and{" "}
                <Link to="/new-braunfels" className="font-semibold text-navy-deep underline">New Braunfels</Link>, and weekend rigs running I-10 and I-35.
              </p>
            </article>

            <article className="border-l-4 border-red bg-white p-6 shadow-card">
              <h3 className="font-display text-xl font-bold text-navy-deep">
                Hunting Truck Builds in the Hill Country
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Ranch and lease trucks for the Texas Hill Country — <Link to="/boerne" className="font-semibold text-navy-deep underline">Boerne</Link>,{" "}
                <Link to="/kerrville" className="font-semibold text-navy-deep underline">Kerrville</Link> and{" "}
                <Link to="/fredericksburg" className="font-semibold text-navy-deep underline">Fredericksburg</Link> — built for axis, whitetail and exotic hunts.
              </p>
            </article>

            <article className="border-l-4 border-red bg-white p-6 shadow-card">
              <h3 className="font-display text-xl font-bold text-navy-deep">
                Dog Boxes &amp; Welding in Austin
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Custom aluminum and steel dog boxes for <Link to="/austin" className="font-semibold text-navy-deep underline">Austin</Link>-area
                hunters — short bed, long bed and flatbed builds welded in our Seguin shop, an
                easy run down 130/183.
              </p>
            </article>

            <article className="border-l-4 border-red bg-white p-6 shadow-card">
              <h3 className="font-display text-xl font-bold text-navy-deep">
                South Texas Hog &amp; Deer Dog Trucks
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Heavy-duty steel dog boxes and ranch truck builds for South Texas brush country —{" "}
                <Link to="/pleasanton" className="font-semibold text-navy-deep underline">Pleasanton</Link>,{" "}
                <Link to="/uvalde" className="font-semibold text-navy-deep underline">Uvalde</Link>,{" "}
                <Link to="/hondo" className="font-semibold text-navy-deep underline">Hondo</Link>,{" "}
                <Link to="/laredo" className="font-semibold text-navy-deep underline">Laredo</Link>, Cotulla, Pearsall and the King Ranch region.
              </p>
            </article>

            <article className="border-l-4 border-red bg-white p-6 shadow-card">
              <h3 className="font-display text-xl font-bold text-navy-deep">
                Gulf Coast Bird &amp; Waterfowl Dog Boxes
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Vented aluminum dog boxes built for the humid Gulf Coast — <Link to="/houston" className="font-semibold text-navy-deep underline">Houston</Link>,{" "}
                <Link to="/corpus-christi" className="font-semibold text-navy-deep underline">Corpus Christi</Link>,{" "}
                <Link to="/rockport" className="font-semibold text-navy-deep underline">Rockport</Link>,{" "}
                <Link to="/victoria" className="font-semibold text-navy-deep underline">Victoria</Link>, Matagorda and Port O'Connor — for retrievers and bird dogs.
              </p>
            </article>

            <article className="border-l-4 border-red bg-white p-6 shadow-card">
              <h3 className="font-display text-xl font-bold text-navy-deep">
                Seguin, TX Welding Shop — Drive In &amp; Roll Out
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">
                On-site fitment in <Link to="/seguin" className="font-semibold text-navy-deep underline">Seguin</Link> — bring the truck, leave with the rig. TIG and MIG welding, marine-vinyl
                upholstery and fitted wrap tarps under one roof.
              </p>
            </article>
          </div>

          <div className="mt-12 border-t-2 border-navy-deep/10 pt-8">
            <h3 className="font-display text-xl font-bold text-navy-deep md:text-2xl">
              Dog Box &amp; Hunting Truck Build Service Areas
            </h3>
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Click your area for local welding, canvas and shade work — every city below
              is served from our Seguin, TX shop.
            </p>
            <ul className="mt-6 flex flex-wrap gap-2 text-sm">
              {[
                { to: "/seguin", label: "Seguin" },
                { to: "/san-antonio", label: "San Antonio" },
                { to: "/new-braunfels", label: "New Braunfels" },
                { to: "/austin", label: "Austin" },
                { to: "/san-marcos", label: "San Marcos" },
                { to: "/schertz", label: "Schertz" },
                { to: "/cibolo", label: "Cibolo" },
                { to: "/boerne", label: "Boerne" },
                { to: "/bulverde", label: "Bulverde" },
                { to: "/spring-branch", label: "Spring Branch" },
                { to: "/canyon-lake", label: "Canyon Lake" },
                { to: "/wimberley", label: "Wimberley" },
                { to: "/kerrville", label: "Kerrville" },
                { to: "/fredericksburg", label: "Fredericksburg" },
                { to: "/hondo", label: "Hondo" },
                { to: "/uvalde", label: "Uvalde" },
                { to: "/pleasanton", label: "Pleasanton" },
                { to: "/laredo", label: "Laredo" },
                { to: "/victoria", label: "Victoria" },
                { to: "/rockport", label: "Rockport" },
                { to: "/corpus-christi", label: "Corpus Christi" },
                { to: "/houston", label: "Houston" },
              ].map((c) => (
                <li key={c.to}>
                  <Link
                    to={c.to}
                    className="inline-block border-2 border-navy-deep bg-white px-3 py-1.5 font-display text-xs uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white"
                  >
                    Dog Boxes in {c.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section
        id="dog-box-quote"
        aria-labelledby="dog-box-quote-heading"
        className="scroll-mt-24 border-t-4 border-navy-deep bg-cream py-20"
      >
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <div className="text-center">
            <div className="font-display text-sm tracking-[0.3em] text-red">
              ★ NO-PRESSURE WRITTEN ESTIMATE
            </div>
            <h2
              id="dog-box-quote-heading"
              className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl"
            >
              Get a Free Dog Box Quote
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
              Tell us your truck make/model, bed size, dog count and any extras —
              same-business-day reply from our Seguin, TX welding shop.
            </p>
          </div>
          <div className="mt-10">
            <DogBoxQuoteForm />
          </div>
        </div>
      </section>

    </ServicePage>
  ),
});
